import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';
function buildTree(folders) {
    var _a, _b;
    const byParent = new Map();
    for (const f of folders) {
        const key = (_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null;
        const bucket = (_b = byParent.get(key)) !== null && _b !== void 0 ? _b : [];
        bucket.push(f);
        byParent.set(key, bucket);
    }
    for (const bucket of byParent.values()) {
        bucket.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    const walk = (parent, depth) => {
        var _a;
        const direct = (_a = byParent.get(parent)) !== null && _a !== void 0 ? _a : [];
        return direct.map(f => ({
            ...f,
            depth,
            children: walk(f.id, depth + 1),
        }));
    };
    return walk(null, 1);
}
// Path from selected folder up to root (inclusive on both ends), so the
// initial expansion can unfurl just enough to reveal the current selection.
// Returns an empty set when no folder is selected or the ID is unknown.
function ancestorIdsOf(folders, folderId) {
    var _a, _b;
    const byId = new Map(folders.map(f => [f.id, f]));
    const out = new Set();
    let cursor = (_a = byId.get(folderId)) === null || _a === void 0 ? void 0 : _a.parentFolderId;
    while (cursor) {
        out.add(cursor);
        cursor = (_b = byId.get(cursor)) === null || _b === void 0 ? void 0 : _b.parentFolderId;
    }
    return out;
}
// Inline tree-style folder picker. Mirrors the visual vocabulary of the
// control panel's UserFolderSection (chevron + colored folder icon + name,
// depth-indented) but is read-only and select-only — no DnD, visibility,
// renames, or action icons. The intent is "show me where I'm saving this"
// at a glance, with the same mental model as the panel.
export const FolderTreePicker = ({ folders, selectedFolderId, onSelect, disabled, maxHeightClass = 'max-h-48', }) => {
    const tree = useMemo(() => buildTree(folders), [folders]);
    // Expansion is local to the picker. Auto-expand all ancestors of the
    // currently-selected folder so the selection is always visible on open.
    // Re-runs only when the selection changes, so user-driven collapses
    // aren't clobbered by re-renders.
    const [expanded, setExpanded] = useState(() => ancestorIdsOf(folders, selectedFolderId));
    useEffect(() => {
        if (!selectedFolderId)
            return;
        setExpanded(prev => {
            const next = new Set(prev);
            for (const id of ancestorIdsOf(folders, selectedFolderId))
                next.add(id);
            return next;
        });
        // Intentionally don't include `folders` so user-driven collapses persist
        // when folder data refetches.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolderId]);
    const toggleExpand = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const renderNode = (node) => {
        const hasChildren = node.children.length > 0;
        const isExpanded = expanded.has(node.id);
        const isSelected = node.id === selectedFolderId;
        return (_jsxs(React.Fragment, { children: [_jsxs("button", { type: "button", onClick: () => { if (!disabled)
                        onSelect(node.id); }, disabled: disabled, className: "w-full flex items-center gap-1.5 px-2 py-1 text-xs text-left transition-colors", style: {
                        marginLeft: (node.depth - 1) * 12,
                        // Selected row uses charcoal (matches panel header bg) + accent text.
                        // Unselected rows are transparent with hover tint so the picker
                        // doesn't visually compete with the rest of the dialog.
                        backgroundColor: isSelected ? 'var(--forest)' : 'transparent',
                        color: isSelected ? '#FFFFFF' : 'var(--sand)',
                        borderRadius: 4,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }, onMouseEnter: (e) => {
                        if (!isSelected && !disabled)
                            e.currentTarget.style.backgroundColor = 'var(--pewter)';
                    }, onMouseLeave: (e) => {
                        if (!isSelected)
                            e.currentTarget.style.backgroundColor = 'transparent';
                    }, title: node.name, children: [hasChildren ? (_jsx("span", { role: "button", tabIndex: -1, onClick: (e) => { e.stopPropagation(); if (!disabled)
                                toggleExpand(node.id); }, className: "flex items-center justify-center", style: { width: 14, height: 14, color: 'currentColor' }, "aria-label": isExpanded ? 'Collapse' : 'Expand', children: isExpanded ? _jsx(ChevronDown, { size: 12 }) : _jsx(ChevronRight, { size: 12 }) })) : (_jsx("span", { style: { width: 14, height: 14, display: 'inline-block' } })), isExpanded && hasChildren
                            ? _jsx(FolderOpen, { size: 12, style: { color: node.color, flexShrink: 0 } })
                            : _jsx(Folder, { size: 12, style: { color: node.color, flexShrink: 0 } }), _jsx("span", { className: "truncate flex-1 min-w-0", children: node.name })] }), isExpanded && hasChildren && node.children.map(renderNode)] }, node.id));
    };
    if (tree.length === 0) {
        return (_jsx("div", { className: "px-2 py-3 rounded text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--pewter)' }, children: "No folders yet \u2014 create one below." }));
    }
    return (_jsx("div", { className: `${maxHeightClass} overflow-y-auto rounded space-y-0.5 py-1`, style: {
            backgroundColor: 'var(--charcoal)',
            border: '1px solid var(--pewter)',
        }, children: tree.map(renderNode) }));
};
